import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import moment from 'moment';
import isEmpty from 'lodash-es/isEmpty';
import cloneDeep from 'lodash-es/cloneDeep';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FieldDef, QueryConfig } from 'app/core/models';
import { Dashboard, DashboardType } from 'app/modules/dashboard/dashboard.types';
import { AddChartComponent } from 'app/modules/dashboard/add-chart/add-chart.component';
import { ProjectsFilterComponent } from 'app/modules/dashboard/projects-filter/projects-filter.component';
import { DashboardService } from 'app/modules/dashboard/dashboard.service';
import { getAllowedFields } from 'app/shared/functions';
import { RelatedToType } from 'app/shared/enums';
import { ReportLayoutChart } from 'app/modules/reporting/reporting.types';
import { ChartType } from 'app/shared/components/chart/chart.types';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { filter } from 'rxjs/operators';
import { AuthService } from 'app/core';
import { FeatureFlag, NewPermission, Permission } from 'app/core/enums';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { Store } from '@ngxs/store';

@Component({
  selector: 'dashboard-details',
  templateUrl: './details.component.html',
  styles: [
    `
      :host {
        width: 100%;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardDetailsComponent extends OnDestroyMixin implements OnInit {
  public data: Data;
  public dashboard: Dashboard;
  public charts: Array<ReportLayoutChart[]> = [];
  public isLegacy: boolean;
  public editMode = false;
  public editingName = false;
  public runningFilter = false;

  protected readonly NewPermission = NewPermission;
  protected readonly Permission = Permission;
  protected readonly newPermissionsEnabled$ = this.store.select(
    AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS),
  );

  private displayColumns: string[] = [];
  private queryFilters: Partial<QueryConfig.ChartFilters>;
  private fieldDefs: FieldDef[] = [];
  private chartDefinition: any;

  get dashboardName(): string {
    return this.dashboard?.name ?? 'Untitled';
  }

  set dashboardName(value: string) {
    if (this.dashboard) {
      this.dashboard.name = value;
    }
  }

  constructor(
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dashboardService: DashboardService,
    private readonly matDialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly confirmService: ConfirmService,
    private readonly authService: AuthService,
    private readonly store: Store,
  ) {
    super();

    this.queryFilters = {
      createdAtGteq: moment(moment().subtract(30, 'days')).format('YYYY-MM-DD'),
      createdAtLteq: moment(moment().add(1, 'days')).format('YYYY-MM-DD'),
    };

    this.displayColumns = ['title', 'project_stage_id', 'created_at'];

    this.charts = [
      [
        {
          id: '123',
          title: 'Deals by status',
          chart_type: ChartType.Bar,
          summary_type: 'total',
          summary_value: 'id',
          group_by: 'project_stage_id',
          data: {},
        },
        {
          id: '1236',
          title: 'Deals by Creation Date',
          chart_type: ChartType.Line,
          summary_type: 'total',
          summary_value: 'id',
          group_by: 'created_at',
          data: {},
        },
      ],
      [
        {
          id: '124',
          title: 'Deals by region',
          chart_type: ChartType.HorizontalBar,
          summary_type: 'total',
          summary_value: 'id',
          group_by: 'region',
          data: {},
        },
        {
          id: '1236',
          title: 'Deals by Property Type',
          chart_type: ChartType.Donut,
          summary_type: 'total',
          summary_value: 'id',
          group_by: 'property_type_id',
          data: {},
        },
        {
          id: '1236',
          title: 'Deals by User',
          chart_type: ChartType.HorizontalBar,
          summary_type: 'total',
          summary_value: 'id',
          group_by: 'assigned_user_id',
          data: {},
        },
      ],
    ];
  }

  ngOnInit(): void {
    this.activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.data = data;
      this.setInitialDashboardValues(this.data.dashboard);
    });
    this.calcStartAndEndDates(this.queryFilters);
  }

  private setInitialDashboardValues(dashboard: Dashboard): void {
    this.dashboard = dashboard;
    this.isLegacy = this.dashboard.dashboard_type === DashboardType.Legacy;
    this.chartDefinition = JSON.parse(this.dashboard.definition);
    this.charts = this.chartDefinition.charts;
    if (this.chartDefinition.filters) {
      this.queryFilters = this.chartDefinition.filters;
    } else {
      this.queryFilters = { period: 'last_30_days' };
    }
    if (this.chartDefinition.displayColumns) {
      this.displayColumns = this.chartDefinition.displayColumns;
    }

    this.applyFilters();
    this.fieldDefs = getAllowedFields(this.data.fieldDefs, RelatedToType.Project);
  }

  private calcStartAndEndDates(filters: Partial<QueryConfig.ChartFilters>): void {
    const period = filters.period;
    this.queryFilters = filters;

    switch (period) {
      case 'this_week':
        this.queryFilters.createdAtGteq = moment(moment().startOf('isoWeek')).format('YYYY-MM-DD');
        break;

      case 'this_month':
        this.queryFilters.createdAtGteq = moment(moment().startOf('month')).format('YYYY-MM-DD');
        break;

      case 'last_15_days':
        this.queryFilters.createdAtGteq = moment(moment().subtract(15, 'days')).format('YYYY-MM-DD');
        break;

      case 'last_30_days':
        this.queryFilters.createdAtGteq = moment(moment().subtract(30, 'days')).format('YYYY-MM-DD');
        break;

      case 'last_60_days':
        this.queryFilters.createdAtGteq = moment(moment().subtract(60, 'days')).format('YYYY-MM-DD');
        break;

      case 'last_90_days':
        this.queryFilters.createdAtGteq = moment(moment().subtract(90, 'days')).format('YYYY-MM-DD');
        break;

      case 'this_year':
        this.queryFilters.createdAtGteq = moment(moment().startOf('year')).format('YYYY-MM-DD');
        break;

      case 'all_time':
        this.queryFilters.createdAtGteq = moment(moment().subtract(10, 'years')).format('YYYY-MM-DD');
        break;

      default:
        this.queryFilters.createdAtLteq = moment(moment().add(1, 'days')).format('YYYY-MM-DD');
        break;
    }
  }

  private applyFilters(): void {
    //do not run if already running
    if (!this.runningFilter) {
      this.runningFilter = true;
      let filters = cloneDeep(this.queryFilters);
      delete filters.period;

      this.dashboardService.getDashboardData(this.dashboard.id).subscribe((data) => {
        this.charts = cloneDeep(data);
        this.runningFilter = false;
        this.cdRef.markForCheck();
      });
    }
  }

  public editName(): void {
    this.editingName = !this.editingName;

    if (!this.editingName) {
      if (!this.dashboard?.name?.trim()?.length) {
        this.dashboard.name = 'Untitled';
      }
    }
  }

  public toggleEditMode(): void {
    this.editMode = !this.editMode;
  }

  public deleteDashboard(): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this.dashboardService
          .deleteDashboard(this.dashboard.id)
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => {
            let route = this.activatedRoute;
            this.router.navigate(['../'], { relativeTo: route });
          });
      });
  }

  public saveDashboard(): void {
    if (this.isLegacy) {
      this.chartDefinition.charts = this.charts;
      this.chartDefinition.filters = this.queryFilters;
      this.chartDefinition.displayColumns = this.displayColumns;
      this.dashboard.definition = JSON.stringify(this.chartDefinition);

      if (!this.dashboard?.name?.trim().length) {
        this.dashboard.name = 'Untitled';
      }

      this.dashboardService
        .updateDashboard(this.dashboard)
        .pipe(untilComponentDestroyed(this))
        .subscribe(() => {
          this.editMode = false;
          this.editingName = false;
          this.applyFilters();
          this.snackBar.open('Dashboard has been saved.', '', {
            duration: 3000,
          });
          this.cdRef.markForCheck();
        });
    }
  }

  public addChart(row: number): void {
    this.matDialog
      .open(AddChartComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { fieldDefs: this.fieldDefs, options: this.data.options },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.chart) {
          this.charts[row].push(result.chart);
          this.cdRef.markForCheck();
        }
      });
  }

  public removeChart(row: number, col: number): void {
    this.charts[row].splice(col, 1);
    if (isEmpty(this.charts[row])) {
      this.charts.pop();
    }
    this.cdRef.markForCheck();
  }

  public editChart(row: number, col: number, chartDef: ReportLayoutChart, saveOnClose = false): void {
    this.matDialog
      .open(AddChartComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { fieldDefs: this.fieldDefs, chart: chartDef, options: this.data.options },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.chart) {
          this.charts[row][col] = result.chart;
          if (saveOnClose) {
            this.saveDashboard();
          }
          this.cdRef.markForCheck();
        }
      });
  }

  public addRow(): void {
    this.matDialog
      .open(AddChartComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { fieldDefs: this.fieldDefs, options: this.data.options },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.chart) {
          this.charts.push([result.chart]);
          this.cdRef.markForCheck();
        }
      });
  }

  public openProjectsFilterDialog(): void {
    this.matDialog
      .open(ProjectsFilterComponent, {
        panelClass: 'mailbox-compose-dialog',
        data: { data: this.data, filters: this.queryFilters, savedFilter: {} },
      })
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.filters) {
          this.cdRef.markForCheck();
          this.calcStartAndEndDates(result.filters);
          this.saveDashboard();
        } else {
          let url = this.router.url.split('?')[0];
          this.router.navigate([url], { queryParams: { filterId: 'reset' } });
        }
      });
  }

  canShowThreeDotMenu(): boolean {
    if (this.editMode) {
      return this.authService.hasPermission(Permission.DASHBOARDS_DELETE);
    } else {
      return this.authService.hasAnyPermission(Permission.DASHBOARDS_CREATE_EDIT, Permission.DASHBOARDS_DELETE);
    }
  }

  canEditDashboard(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.DASHBOARDS_EDIT));
    }
    return this.authService.hasPermission(Permission.DASHBOARDS_CREATE_EDIT);
  }
}
